import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useRootStore } from "store";

import { ChevronDown16Filled } from "@fluentui/react-icons";
import { InboxType, inboxViews } from "models/inbox/inbox";

import { useRequiredCustomerId } from "hooks/use-customer-id";
import "./InboxDropdownPanel.scss";

export default function InboxDropdownPanel() {
  const navigate = useNavigate();
  const { inboxType } = useParams<{ inboxType: InboxType }>();
  const customerId = useRequiredCustomerId();

  const userInfo = useRootStore((s) => s.user.userInfo);
  const isInternal = userInfo?.type === "Internal";
  const [open, setOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  const tabs = isInternal
    ? inboxViews
    : inboxViews.filter((t) => t.value !== "all");

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Sync selected value with URL
  useEffect(() => {
    if (inboxType) {
      setSelectedValue(inboxType);
    } else {
      setSelectedValue(tabs[0].value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inboxType]);

  const selectedLabel =
    tabs.find((t) => t.value === selectedValue)?.label ?? tabs[0].label;

  const handleChange = (newValue: InboxType) => {
    setSelectedValue(newValue);
    setOpen(false);
    navigate(`/customers/${customerId}/inbox/${newValue}`);
  };

  return (
    <div className="custom-select" ref={dropdownRef}>
      <button
        type="button"
        className={`custom-select-trigger ${open ? "open" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span>{selectedLabel}</span>
        <ChevronDown16Filled className="select-icon" />
      </button>

      {open && (
        <div className="custom-select-options">
          {tabs.map((tab) => (
            <div
              key={tab.value}
              className={`custom-option ${
                tab.value === selectedValue ? "selected" : ""
              }`}
              onClick={() => handleChange(tab.value)}
            >
              {tab.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
